import React from 'react';

interface SkeletonLoaderProps {
  width?: string | number;
  height?: string | number;
  variant?: 'text' | 'rect' | 'circle';
  animation?: 'pulse' | 'wave' | 'none';
  className?: string;
  count?: number;
  spacing?: string;
}

export const SkeletonLoader: React.FC<SkeletonLoaderProps> = ({
  width = '100%',
  height = '20px',
  variant = 'rect',
  animation = 'pulse',
  className = '',
  count = 1,
  spacing = 'var(--space-2)'
}) => {
  const getBorderRadius = () => {
    switch (variant) {
      case 'circle':
        return '50%';
      case 'text':
        return 'var(--radius-sm)';
      default:
        return 'var(--radius-md)';
    }
  };

  const getAnimation = () => {
    switch (animation) {
      case 'pulse':
        return 'skeleton-pulse 1.5s ease-in-out infinite';
      case 'wave':
        return 'skeleton-wave 1.6s linear infinite';
      default:
        return 'none';
    }
  };

  const skeletonStyle: React.CSSProperties = {
    width: typeof width === 'number' ? `${width}px` : width,
    height: typeof height === 'number' ? `${height}px` : height,
    backgroundColor: 'var(--color-gray-200)',
    borderRadius: getBorderRadius(),
    animation: getAnimation(),
    display: 'block',
    position: 'relative',
    overflow: 'hidden'
  };

  const skeletons = Array.from({ length: count }, (_, index) => (
    <div
      key={index}
      style={{
        ...skeletonStyle,
        marginBottom: index < count - 1 ? spacing : 0
      }}
      aria-label="Loading content"
      role="status"
    />
  ));

  return <>{skeletons}</>;
};

// Table skeleton component
export const TableSkeleton: React.FC<{ rows?: number; columns?: number }> = ({
  rows = 5,
  columns = 4
}) => {
  return (
    <div style={{ padding: 'var(--space-4)' }}>
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: `repeat(${columns}}, 1fr)`,
        gap: 'var(--space-3)',
        marginBottom: 'var(--space-3)',
        paddingBottom: 'var(--space-3)',
        borderBottom: '1px solid var(--color-border)'
      }}>
        {Array.from({ length: columns }, (_, i) => (
          <SkeletonLoader key={i} height="24px" width="80%" />
        ))}
      </div>
      {Array.from({ length: rows }, (_, rowIndex) => (
        <div
          key={rowIndex}
          style={{
            display: 'grid',
            gridTemplateColumns: `repeat(${columns}}, 1fr)`,
            gap: 'var(--space-3)',
            padding: 'var(--space-3) 0',
            borderBottom: '1px solid var(--color-border-light)'
          }}
        >
          {Array.from({ length: columns }, (_, colIndex) => (
            <SkeletonLoader 
              key={colIndex} 
              height="20px" 
              width={colIndex === 0 ? '100%' : '60%'}
            />
          ))}
        </div>
      ))}
    </div>
  );
};

// Card skeleton component
export const CardSkeleton: React.FC = () => {
  return (
    <div style={{
      padding: 'var(--space-4)',
      borderRadius: 'var(--radius-lg)',
      border: '1px solid var(--color-border)',
      background: 'var(--color-surface)'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', marginBottom: 'var(--space-3)' }}>
        <SkeletonLoader variant="circle" width={40} height={40} />
        <div style={{ flex: 1 }}>
          <SkeletonLoader height="20px" width="60%" />
          <SkeletonLoader height="16px" width="40%" spacing="var(--space-1)" />
        </div>
      </div>
      <SkeletonLoader height="60px" />
      <div style={{ display: 'flex', gap: 'var(--space-2)', marginTop: 'var(--space-3)' }}>
        <SkeletonLoader height="32px" width="80px" />
        <SkeletonLoader height="32px" width="80px" />
      </div>
    </div>
  );
};

// Add animations to global styles
const style = document.createElement('style');
style.textContent = `
  @keyframes skeleton-pulse {
    0% {
      opacity: 1;
    }
    50% {
      opacity: 0.4;
    }
    100% {
      opacity: 1;
    }
  }

  @keyframes skeleton-wave {
    0% {
      transform: translateX(-100%);
    }
    50%, 100% {
      transform: translateX(100%);
    }
  }

  .skeleton-loader::after {
    content: "";
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    transform: translateX(-100%);
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.3),
      transparent
    );
    animation: skeleton-wave 1.6s linear infinite;
  }
`;
document.head.appendChild(style);